import * as React from 'react'
import { useIntl } from 'react-intl'
import { rem } from 'polished'
import styled from 'styled-components'
import noop from 'lib/function/noop'
import BodyText from 'LuxKit/Typography/BodyText'
import IconButton from 'LuxKit/Button/IconButton'
import TextButton from 'LuxKit/Button/TextButton'
import LineInfoCircleIcon from 'LuxKit/Icons/line/LineInfoCircleIcon'
import LineShoppingCartIcon from 'LuxKit/Icons/line/LineShoppingCartIcon'
import PriceRowLoyaltyPoints from 'LuxKit/PricePoints/Loyalty/PriceRowLoyaltyPoints'
import PriceRowTaxesAndFees from 'LuxKit/PricePoints/PriceRowTaxesAndFees'
import Tooltip from 'LuxKit/Tooltip'
import FormatCurrency from 'components/Common/FormatCurrency'
import { mediaQueryUp } from 'components/utils/breakpoint'

const BookingFooter = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) auto auto;
  grid-template-rows: auto auto;
  align-items: center;
  column-gap: ${rem(12)};
  row-gap: ${rem(4)};
  padding-top: ${rem(20)};
  border-top: 1px solid var(--palette-neutral-surface-light-foreground);

  ${mediaQueryUp.tablet} {
    display: flex;
    gap: ${rem(20)};
  }
`

const BookingFooterCopy = styled.div`
  grid-column: 1;
  grid-row: 1;
  flex-shrink: 0;
  min-width: 0;
`

const EarnPoints = styled.div`
  display: flex;
  grid-column: 1;
  grid-row: 2;
  flex: 1;
  align-items: center;
  gap: ${rem(4)};
  min-width: 0;
`

const BookNowButton = styled(TextButton)`
  grid-column: 3;
  grid-row: 1 / span 2;
  white-space: nowrap;
  --button-color: var(--palette-highlight-primary-dark-contrast);
  --button-background-color: var(--palette-highlight-primary-dark-background);
  --button-border-color: var(--palette-highlight-primary-dark-background);
  --button-hover-color: var(--palette-highlight-primary-dark-contrast);
  --button-hover-background-color: color-mix(in srgb, var(--palette-highlight-primary-dark-background), var(--palette-neutral-control-color-white) var(--palette-neutral-control-opacity-8));
  --button-hover-border-color: var(--button-hover-background-color);
  --button-active-color: var(--palette-highlight-primary-dark-contrast);
  --button-active-background-color: color-mix(in srgb, var(--palette-highlight-primary-dark-background), var(--palette-neutral-control-color-black) var(--palette-neutral-control-opacity-8));
  --button-active-border-color: var(--button-active-background-color);
`

const CartButton = styled(IconButton)`
  grid-column: 2;
  grid-row: 1 / span 2;
  --button-internal-default-color: var(--palette-highlight-primary-dark-foreground);
  --button-internal-default-background-color: var(--palette-neutral-surface-static-white);
  --button-internal-default-border-color: var(--palette-neutral-surface-light-foreground);
  --button-internal-hover-color: var(--palette-highlight-primary-dark-foreground);
  --button-internal-hover-background-color: var(--palette-neutral-surface-light-background);
  --button-internal-hover-border-color: var(--palette-highlight-primary-dark-foreground);
  --button-internal-active-color: var(--palette-highlight-primary-dark-foreground);
  --button-internal-active-background-color: var(--palette-neutral-surface-light-background);
  --button-internal-active-border-color: var(--palette-highlight-primary-dark-foreground);
`

const SeatPrice = styled(BodyText)`
  white-space: nowrap;

  && {
    font-size: 20px;
    font-weight: 450;
    line-height: 24px;
  }
`

const TotalPrice = styled.div`
  display: flex;
  align-items: baseline;
  gap: ${rem(2)};
`

interface Props {
  totalPrice: number
  displayCurrencyCode: string
  earnablePoints: number
  disabled: boolean
  onContinue?: () => void
}

function ArenaBookingFooter({ totalPrice, displayCurrencyCode, earnablePoints, disabled, onContinue = noop }: Props) {
  const intl = useIntl()
  const totalLabel = intl.formatMessage({
    id: 'AqxTyVgxaMgFk9YTgJfC',
    defaultMessage: 'total',
  })
  const loyaltyPointsTooltip = intl.formatMessage({
    id: 'qt2bdxh29TEr3_NYydFC',
    defaultMessage: 'Loyalty points are earned after your booking is completed.',
  })
  const addToCartLabel = intl.formatMessage({
    id: 'S02_Q7wrY0r14pbuTWqx',
    defaultMessage: 'Add to cart',
  })
  const continueLabel = intl.formatMessage({
    id: 'eCd5w1sk8iA15UbrgEt2',
    defaultMessage: 'Book now',
  })

  return (
    <BookingFooter>
      <BookingFooterCopy>
        <TotalPrice>
          <SeatPrice as="span" variant="large"><FormatCurrency value={totalPrice} currency={displayCurrencyCode} /></SeatPrice>
          <BodyText as="span" variant="small" colour="neutral-two">{totalLabel}</BodyText>
        </TotalPrice>
        <PriceRowTaxesAndFees />
      </BookingFooterCopy>
      <EarnPoints>
        <PriceRowLoyaltyPoints
          earnablePoints={earnablePoints}
          calculationType="exact"
          size="S"
          tier="bronze"
        />
        <Tooltip description={loyaltyPointsTooltip}>
          <LineInfoCircleIcon colour="neutral-two" size="S" />
        </Tooltip>
      </EarnPoints>
      <CartButton
        aria-label={addToCartLabel}
        kind="secondary"
        nonInteractable
        shape="circle"
        size="large"
        variant="default"
      >
        <LineShoppingCartIcon />
      </CartButton>
      <BookNowButton kind="primary" shape="round" size="large" disabled={disabled} onClick={onContinue}>
        {continueLabel}
      </BookNowButton>
    </BookingFooter>
  )
}

export default ArenaBookingFooter
